import type { ApiEndpoints } from '@avisoma/shared';

export type StageDef = { id: string; label: string };

export type LaneDef = {
  label: string;
  stages: StageDef[];
  /** Renders below a dashed divider, no inter-lane arrow */
  offPipeline?: boolean;
  /** Omit this lane from the header ETA. Independent of `offPipeline`. */
  excludeFromEta?: boolean;
};

/** Flags from GET /api/system/pipeline-status. All false when the field is omitted. */
export type PipelineControlPlane = NonNullable<
  ApiEndpoints<string>['/api/system/pipeline-status']['GET']['response']['controlPlane']
>;

export const DEFAULT_CONTROL_PLANE: PipelineControlPlane = {
  pageClassWorker: false,
  documentWorker: false,
  projectWorker: false,
};

const splitter: StageDef = { id: 'pdf-splitter', label: 'PDF Splitter' };
const imageProcessor: StageDef = { id: 'image-processor', label: 'Image Processor' };
const pageSegmentation: StageDef = { id: 'page-segmentation', label: 'Page Segmentation' };
const pdfPageParser: StageDef = { id: 'pdf-page-parser', label: 'PDF Page Parser' };
const pageMetadata: StageDef = { id: 'page-metadata', label: 'Page Metadata' };
const pageEmbeddings: StageDef = { id: 'page-embeddings', label: 'Page Embeddings' };
const pageClassification: StageDef = { id: 'page-classification', label: 'Page Classification' };
const pageWorker: StageDef = { id: 'page-worker', label: 'Page Worker' };
const documentMetadata: StageDef = { id: 'document-metadata', label: 'Document Metadata' };
const documentEmbeddings: StageDef = { id: 'document-embeddings', label: 'Document Embeddings' };
const documentClassification: StageDef = { id: 'document-classification', label: 'Document Classification' };
const documentReconstruction: StageDef = { id: 'document-reconstruction', label: 'Document Reconstruction' };
const documentWorker: StageDef = { id: 'document-worker', label: 'Document Worker' };
const dataExtraction: StageDef = { id: 'data-extraction', label: 'Data Extraction' };
const finalizeProject: StageDef = { id: 'finalize-project', label: 'Finalize Project' };
const projectWorker: StageDef = { id: 'project-worker', label: 'Project Worker' };
const webhook: StageDef = { id: 'webhook', label: 'Webhook' };
const outboxRelay: StageDef = { id: 'outbox-relay', label: 'Outbox Relay' };
const other: StageDef = { id: 'other', label: 'Other' };
const report: StageDef = { id: 'report', label: 'Report' };

const PAGE_HOPS = [pageSegmentation, pdfPageParser, pageMetadata, pageEmbeddings, pageClassification];
const PAGE_CLASS_HOPS = [pdfPageParser, pageMetadata, pageEmbeddings, pageClassification];
const DOCUMENT_HOPS = [documentMetadata, documentEmbeddings, documentClassification, documentReconstruction];

/** Every stage the charts can label, including drain and control-plane nodes. */
export const ALL_STAGE_DEFS: StageDef[] = [
  splitter,
  imageProcessor,
  ...PAGE_HOPS,
  pageWorker,
  ...DOCUMENT_HOPS,
  documentWorker,
  dataExtraction,
  finalizeProject,
  projectWorker,
  webhook,
  outboxRelay,
  other,
  report,
];

export const pipelineLanes = (flags: PipelineControlPlane = DEFAULT_CONTROL_PLANE): LaneDef[] => {
  const dark: StageDef[] = [];
  const drain: StageDef[] = [];
  if (flags.pageClassWorker) drain.push(...PAGE_CLASS_HOPS);
  else dark.push(pageWorker);
  if (flags.documentWorker) drain.push(...DOCUMENT_HOPS);
  else dark.push(documentWorker);
  if (flags.projectWorker) drain.push(finalizeProject);
  else dark.push(projectWorker);
  dark.push(outboxRelay);

  const lanes: LaneDef[] = [
    { label: 'Intake', stages: [splitter, imageProcessor] },
    {
      label: 'Per page',
      stages: flags.pageClassWorker ? [pageSegmentation, pageWorker] : PAGE_HOPS,
    },
    {
      label: 'Document',
      stages: flags.documentWorker ? [documentWorker] : DOCUMENT_HOPS,
    },
    {
      label: 'Entity / Project',
      stages: flags.projectWorker
        ? [dataExtraction, projectWorker, webhook]
        : [dataExtraction, finalizeProject, webhook],
    },
    { label: 'Control plane', offPipeline: true, excludeFromEta: true, stages: dark },
  ];
  if (drain.length > 0) lanes.push({ label: 'Drain', offPipeline: true, stages: drain });
  lanes.push({ label: 'Off-pipeline', offPipeline: true, excludeFromEta: true, stages: [other, report] });
  return lanes;
};

/** Legacy hop graph (all workers off). Charts and tests that need a stable list use this. */
export const PIPELINE_LANES: LaneDef[] = pipelineLanes(DEFAULT_CONTROL_PLANE);

export const onPipelineStageIds = (lanes: LaneDef[]): Set<string> =>
  new Set(lanes.filter((lane) => !lane.excludeFromEta).flatMap((lane) => lane.stages.map((s) => s.id)));

/** Stage ids that belong to the document-processing pipeline (excludes `offPipeline` lanes). */
export const ON_PIPELINE_STAGE_IDS = onPipelineStageIds(PIPELINE_LANES);

/** Chart + stage accent colors per stage id (chart.js needs concrete colors). */
export const STAGE_COLORS: Record<string, string> = {
  'pdf-splitter': '#3b82f6',
  'image-processor': '#60a5fa',
  'page-segmentation': '#818cf8',
  'page-worker': '#4f46e5',
  'pdf-page-parser': '#6366f1',
  'page-metadata': '#8b5cf6',
  'page-embeddings': '#a78bfa',
  'page-classification': '#d946ef',
  'document-worker': '#0d9488',
  'document-metadata': '#14b8a6',
  'document-embeddings': '#10b981',
  'document-classification': '#22c55e',
  'document-reconstruction': '#84cc16',
  'data-extraction': '#eab308',
  'project-worker': '#ea580c',
  'finalize-project': '#f59e0b',
  webhook: '#f97316',
  'outbox-relay': '#78716c',
  other: '#94a3b8',
  report: '#64748b',
};
